import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Users, UserCheck, Clock, FileText, TrendingUp, CalendarCheck,
  AlertTriangle, Building2, Megaphone, Fingerprint, Bell,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS, ATT_STATUS, fmtDate } from "@/lib/constants";
import { Card } from "@/components/ui/card";

function Stat({ icon: Icon, label, value, hint, color, testid }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}
      data-testid={testid}
      className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md dark:border-slate-800 dark:bg-slate-900"
    >
      <div className={`mb-3 flex h-10 w-10 items-center justify-center rounded-xl ${color}`}>
        <Icon className="h-5 w-5" />
      </div>
      <p className="font-mono text-3xl font-bold tracking-tight">{value ?? "-"}</p>
      <p className="mt-1 text-sm font-medium text-slate-600 dark:text-slate-300">{label}</p>
      {hint && <p className="mt-0.5 text-xs text-slate-400">{hint}</p>}
    </motion.div>
  );
}

export default function Dashboard() {
  const { user, profile } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [anns, setAnns] = useState([]);
  const [unread, setUnread] = useState(0);
  const [expiring, setExpiring] = useState([]);
  const [reminders, setReminders] = useState([]);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/announcements").then((r) => setAnns(r.data)).catch(() => {});
    api.get("/announcements/unread-count").then((r) => setUnread(r.data.count)).catch(() => {});
    api.get("/documents/expiring").then((r) => setExpiring(r.data)).catch(() => {});
    const pad = (n) => String(n).padStart(2, "0");
    const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const now = new Date();
    const t = ymd(now);
    const tm = ymd(new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1));
    Promise.all([
      api.get("/events").catch(() => ({ data: [] })),
      api.get("/holidays").catch(() => ({ data: [] })),
    ]).then(([ev, hol]) => {
      const items = [];
      hol.data.forEach((h) => { if (h.date === t || h.date === tm) items.push({ id: "h" + h.id, when: h.date === t ? "Hari ini" : "Besok", label: h.name, kind: "Libur" }); });
      ev.data.forEach((e) => { if (e.date === t || e.date === tm) items.push({ id: "e" + e.id, when: e.date === t ? "Hari ini" : "Besok", label: e.title, kind: "Acara" }); });
      setReminders(items);
    });
  }, []);

  const role = user.role;
  const isHR = ["HR_ADMIN", "HR_SUPERVISOR", "SECRETARIAT", "CEO"].includes(role);
  const isSpv = role === "SUPERVISOR";

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight lg:text-3xl">
          Selamat datang, {profile?.full_name?.split(" ")[0] || "Karyawan"} 👋
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          {ROLE_LABELS[role]} · {fmtDate(new Date())}
        </p>
      </div>

      {unread > 0 && (
        <button data-testid="unread-banner" onClick={() => nav("/pengumuman")}
          className="flex w-full items-center gap-3 rounded-2xl border border-sky-200 bg-sky-50 px-5 py-4 text-left transition-colors hover:bg-sky-100 dark:border-sky-900 dark:bg-sky-950/40">
          <Megaphone className="h-5 w-5 text-primary" />
          <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
            Anda memiliki <span className="font-bold text-primary">{unread}</span> pengumuman baru yang belum dibaca
          </span>
        </button>
      )}

      {reminders.length > 0 && (
        <button data-testid="event-reminder-banner" onClick={() => nav("/kalender")}
          className="flex w-full flex-col gap-1 rounded-2xl border border-primary/30 bg-primary/5 px-5 py-4 text-left transition-colors hover:bg-primary/10">
          <span className="flex items-center gap-2 text-sm font-semibold text-primary"><Bell className="h-4 w-4" /> Pengingat Agenda</span>
          <div className="space-y-0.5">
            {reminders.map((r) => (
              <p key={r.id} data-testid="reminder-item" className="text-sm text-slate-700 dark:text-slate-200">
                <span className={`mr-2 rounded-full px-2 py-0.5 text-[10px] font-medium ${r.when === "Hari ini" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-800"}`}>{r.when}</span>
                <span className="font-medium">{r.kind}:</span> {r.label}
              </p>
            ))}
          </div>
        </button>
      )}

      {expiring.length > 0 && (
        <div data-testid="contract-expiry-alert" className="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 dark:border-amber-900 dark:bg-amber-950/30">
          <p className="flex items-center gap-2 text-sm font-semibold text-amber-800 dark:text-amber-300">
            <AlertTriangle className="h-4 w-4" /> {expiring.length} dokumen kontrak akan / telah berakhir
          </p>
          <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
            {expiring.slice(0, 5).map((d) => `${d.employee_name} — ${d.type_label} (${fmtDate(d.expiry_date)})`).join(" · ")}
          </p>
        </div>
      )}

      {stats?.contract_alerts?.length > 0 && (
        <div data-testid="contract-date-alert" className="rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 dark:border-rose-900 dark:bg-rose-950/30">
          <p className="flex items-center gap-2 text-sm font-semibold text-rose-800 dark:text-rose-300">
            <AlertTriangle className="h-4 w-4" /> {stats.contract_alerts.length} karyawan: kontrak / exit dalam ≤30 hari
          </p>
          <div className="mt-1 space-y-0.5">
            {stats.contract_alerts.slice(0, 6).map((c, i) => (
              <p key={i} data-testid="contract-alert-item" className="text-xs text-rose-700 dark:text-rose-400">
                {c.full_name} — {c.kind} {fmtDate(c.date)} ({c.days_left <= 0 ? "hari ini / lewat" : `${c.days_left} hari lagi`})
              </p>
            ))}
          </div>
        </div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {isHR && stats && (
          <>
            <Stat testid="stat-total-employees" icon={Users} label="Total Karyawan" value={stats.total_employees}
              hint={`${stats.active_employees} aktif`} color="bg-blue-100 text-blue-700" />
            <Stat testid="stat-present-today" icon={UserCheck} label="Hadir Hari Ini" value={stats.present_today}
              color="bg-emerald-100 text-emerald-700" />
            <Stat testid="stat-late-today" icon={Clock} label="Terlambat" value={stats.late_today}
              color="bg-amber-100 text-amber-700" />
            <Stat testid="stat-pending-forms" icon={FileText} label="Formulir Menunggu" value={stats.pending_forms}
              color="bg-rose-100 text-rose-700" />
          </>
        )}
        {isSpv && stats && (
          <>
            <Stat testid="stat-team-size" icon={Users} label="Anggota Tim" value={stats.team_size}
              color="bg-purple-100 text-purple-700" />
            <Stat testid="stat-present-today" icon={UserCheck} label="Hadir Hari Ini" value={stats.present_today}
              color="bg-emerald-100 text-emerald-700" />
            <Stat testid="stat-absent-today" icon={AlertTriangle} label="Belum Hadir" value={stats.absent_today}
              color="bg-rose-100 text-rose-700" />
            <Stat testid="stat-pending-approvals" icon={CalendarCheck} label="Perlu Persetujuan" value={stats.pending_approvals}
              color="bg-amber-100 text-amber-700" />
          </>
        )}
        {!isHR && !isSpv && stats && (
          <>
            <Stat testid="stat-hadir-bulan" icon={CalendarCheck} label="Hadir Bulan Ini" value={stats.hadir_bulan_ini}
              color="bg-emerald-100 text-emerald-700" />
            <Stat testid="stat-terlambat-bulan" icon={Clock} label="Terlambat Bulan Ini" value={stats.terlambat_bulan_ini}
              color="bg-amber-100 text-amber-700" />
            <Stat testid="stat-hari-kerja" icon={TrendingUp} label="Hari Tercatat" value={stats.total_hari_kerja}
              color="bg-blue-100 text-blue-700" />
            <Stat testid="stat-my-forms" icon={FileText} label="Formulir Saya" value={stats.total_forms}
              hint={`${stats.pending_forms} menunggu`} color="bg-indigo-100 text-indigo-700" />
            <Stat testid="stat-late-minutes" icon={AlertTriangle} label="Total Telat (menit)" value={stats.total_late_minutes}
              hint="bulan ini" color="bg-rose-100 text-rose-700" />
          </>
        )}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Chart / breakdown for HR */}
        {isHR && stats?.trend && (
          <Card className="col-span-1 rounded-2xl border-slate-200 p-6 dark:border-slate-800 lg:col-span-2">
            <h3 className="font-heading text-lg font-semibold tracking-tight">Tren Kehadiran 7 Hari</h3>
            <div className="mt-4 h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.trend}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis dataKey="date" tickFormatter={(d) => new Date(d).getDate()} fontSize={12} />
                  <YAxis allowDecimals={false} fontSize={12} />
                  <Tooltip />
                  <Bar dataKey="hadir" fill="#0284C7" radius={[6, 6, 0, 0]} name="Hadir" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            {stats.status_breakdown && (
              <div className="mt-4 flex flex-wrap gap-2">
                {Object.entries(stats.status_breakdown).filter(([, v]) => v > 0).map(([k, v]) => (
                  <span key={k} className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${ATT_STATUS[k]?.cls}`}>
                    {ATT_STATUS[k]?.label}: {v}
                  </span>
                ))}
              </div>
            )}
          </Card>
        )}

        {/* Quick actions for non-HR big role */}
        {!isHR && (
          <Card className="col-span-1 rounded-2xl border-slate-200 bg-slate-900 p-6 text-white lg:col-span-2">
            <h3 className="font-heading text-lg font-semibold tracking-tight">Aksi Cepat</h3>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <button data-testid="quick-absensi" onClick={() => nav("/absensi")}
                className="flex flex-col items-start gap-2 rounded-xl bg-slate-800 p-4 text-left transition-colors hover:bg-slate-700">
                <Fingerprint className="h-5 w-5 text-emerald-400" />
                <span className="text-sm font-medium">Absensi Sekarang</span>
              </button>
              <button data-testid="quick-formulir" onClick={() => nav("/formulir")}
                className="flex flex-col items-start gap-2 rounded-xl bg-slate-800 p-4 text-left transition-colors hover:bg-slate-700">
                <FileText className="h-5 w-5 text-sky-400" />
                <span className="text-sm font-medium">Ajukan Formulir</span>
              </button>
            </div>
          </Card>
        )}

        {/* Announcements */}
        <Card className="col-span-1 rounded-2xl border-slate-200 p-6 dark:border-slate-800">
          <div className="mb-4 flex items-center gap-2">
            <Megaphone className="h-4 w-4 text-primary" />
            <h3 className="font-heading text-lg font-semibold tracking-tight">Pengumuman</h3>
          </div>
          <div className="space-y-3">
            {anns.length === 0 && <p className="text-sm text-slate-400">Belum ada pengumuman.</p>}
            {anns.slice(0, 4).map((a) => (
              <div key={a.id} data-testid="announcement-item" className="rounded-xl border border-slate-100 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-800/40">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold">{a.title}</p>
                  {a.priority === "TINGGI" && (
                    <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-medium text-rose-700">Penting</span>
                  )}
                </div>
                <p className="mt-1 line-clamp-2 text-xs text-slate-500">{a.body}</p>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
